import React, { useRef, useState } from 'react';
import { Button, Modal, Popconfirm } from 'antd';
import { DeleteOutlined, PictureOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { auth } from '../../lib/firebaseAuth';
import { MIN_ADMIN_YETKI } from '../../shared/variables/routes';
import { formatSets, playerName } from '../../shared/utils/league-feed';
import { showNotification } from '../../shared/utils/notifications';
import { PhotoBurst, useDoubleTap, usePhotoState } from './photoEffects';

type Props = {
  event: LeagueFeed.Event;
  viewer?: Users.User;
  tile?: boolean;
  // Akış kartının üstünde tam genişlik (Instagram düzeni).
  hero?: boolean;
  // eslint-disable-next-line no-unused-vars -- callback signature
  onRemoved?: (id: string) => void;
  // Çift dokunma = tebrik (yalnız hero); tek dokunma galeriyi kısa beklemeyle açar.
  onDoubleTap?: () => void;
  burst?: number;
};

export default function MatchPhoto({
  event,
  viewer,
  tile,
  hero,
  onRemoved,
  onDoubleTap,
  burst,
}: Props) {
  const { t, i18n } = useTranslation('leagues');
  const [open, setOpen] = useState(false);
  const [removed, setRemoved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const { veri } = event;
  // Dik fotoğraf geniş kartta 4:3 kırpılınca kafalar kesiliyordu; dikse kare alan + üstten kırpma.
  const photo = usePhotoState(hero ? veri.mac_foto_oran : undefined);
  const tap = useDoubleTap(() => setOpen(true), hero ? onDoubleTap : undefined);
  const inFlight = useRef(false);
  if (removed || !veri.mac_foto_var_mi || !veri.mac_id) return null;
  const src = `/api/league-match-photo?matchId=${encodeURIComponent(veri.mac_id)}${
    retry ? `&retry=${retry}` : ''
  }`;
  const names = `${playerName(veri.kazanan)} · ${playerName(veri.kaybeden)}`;
  const canRemove =
    !!viewer?.id &&
    (viewer.yetki >= MIN_ADMIN_YETKI || [veri.kazanan?.id, veri.kaybeden?.id].includes(viewer.id));
  const remove = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const user = auth.currentUser;
      if (!user || user.uid !== viewer?.uid) throw new Error('PHOTO_AUTH');
      const response = await fetch(src, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${await user.getIdToken()}` },
      });
      if (!response.ok) throw new Error('PHOTO_REMOVE');
      setOpen(false);
      setRemoved(true);
      onRemoved?.(event.id);
    } catch {
      showNotification('error', t('detail.feed.photoRemoveError'));
    } finally {
      setBusy(false);
      inFlight.current = false;
    }
  };
  const error = (
    <div className="match-photo__error" role="status">
      <p>{t('detail.feed.matchPhotoLoadError')}</p>
      <Button
        onClick={() => {
          setFailed(false);
          photo.reset();
          setRetry(value => value + 1);
        }}
      >
        {t('detail.feed.retry')}
      </Button>
    </div>
  );
  return (
    <div
      className={`match-photo${tile ? ' match-photo--tile' : ''}${hero ? ' match-photo--hero' : ''}${
        hero && photo.portrait ? ' is-portrait' : ''
      }`}
    >
      <button
        type="button"
        className={`match-photo__preview${photo.loading ? ' is-loading' : ''}`}
        // Klavyeyle (detail 0) beklemeden açılır; fareyle/dokunarak çift tıklama tebrik eder.
        onClick={click => (click.detail === 0 ? setOpen(true) : tap())}
        aria-haspopup="dialog"
        aria-label={t('detail.photos.open', { players: names })}
      >
        {failed ? (
          <PictureOutlined aria-hidden="true" />
        ) : (
          <img
            src={src}
            alt=""
            width={hero ? 1200 : 144}
            height={hero ? 900 : 104}
            loading="lazy"
            decoding="async"
            ref={photo.imageRef}
            className={photo.loading ? 'is-loading' : undefined}
            onLoad={photo.onLoad}
            onError={() => setFailed(true)}
          />
        )}
      </button>
      {hero && <PhotoBurst burst={burst} />}
      {canRemove && !tile && (
        <Popconfirm
          title={t('detail.photos.removeConfirm')}
          onConfirm={remove}
          okText={t('detail.feed.removeMatchPhoto')}
          cancelText={t('detail.photos.cancel')}
          disabled={busy}
        >
          <Button
            className="match-photo__remove"
            type="text"
            loading={busy}
            icon={<DeleteOutlined aria-hidden="true" />}
            aria-label={t('detail.feed.removeMatchPhoto')}
            title={t('detail.feed.removeMatchPhoto')}
          />
        </Popconfirm>
      )}
      <Modal
        visible={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={720}
        title={t('detail.feed.matchPhotoAlt')}
        className="match-photo-dialog"
        destroyOnClose
      >
        {failed ? (
          error
        ) : (
          <img
            className="match-photo-dialog__image"
            src={src}
            alt={t('detail.photos.open', { players: names })}
            width={1200}
            height={900}
            onError={() => setFailed(true)}
          />
        )}
        {tile && canRemove && (
          <Popconfirm
            title={t('detail.photos.removeConfirm')}
            onConfirm={remove}
            okText={t('detail.feed.removeMatchPhoto')}
            cancelText={t('detail.photos.cancel')}
            disabled={busy}
          >
            <Button
              className="match-photo-dialog__remove"
              type="text"
              loading={busy}
              icon={<DeleteOutlined aria-hidden="true" />}
              aria-label={t('detail.feed.removeMatchPhoto')}
              title={t('detail.feed.removeMatchPhoto')}
            />
          </Popconfirm>
        )}
        <div className="match-photo-dialog__details">
          <strong>{names}</strong>
          <span className="match-photo-dialog__score">{formatSets(veri.setler) || '—'}</span>
          <p>
            {t('detail.photos.winner', {
              name: playerName(veri.kazanan),
              points: veri.puan ?? '—',
            })}
          </p>
          <time dateTime={new Date(event.olusturma).toISOString()}>
            {t('detail.photos.sharedAt', {
              date: new Intl.DateTimeFormat(i18n.language, {
                dateStyle: 'medium',
                timeStyle: 'short',
                timeZone: 'Europe/Istanbul',
              }).format(event.olusturma),
            })}
          </time>
        </div>
      </Modal>
    </div>
  );
}

MatchPhoto.defaultProps = {
  viewer: undefined,
  tile: false,
  hero: false,
  onRemoved: undefined,
  onDoubleTap: undefined,
  burst: undefined,
};
